import { Check, Copy } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { PANEL_TYPE_META } from '@/lib/panels'

import type { BlockType } from './config-types'

/** Small-caps section header inside a pane. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">
      {children}
    </div>
  )
}

/** Label above a form control (visual; use `htmlFor` to tie it to an input). */
export function FieldLabel({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return htmlFor
    ? <label htmlFor={htmlFor} className="block text-[12px] text-text-2">{children}</label>
    : <div className="text-[12px] text-text-2">{children}</div>
}

/** Inline error line under a field. */
export function FieldError({ children }: { children: ReactNode }) {
  if (!children) return null
  return (
    <p className="mt-1 text-[12px] text-danger" role="alert">
      {children}
    </p>
  )
}

/** Helper hint under a control (optionally a warning). */
export function Hint({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'warn' }) {
  return (
    <p className={`mt-1 text-[11.5px] leading-relaxed ${tone === 'warn' ? 'text-warn' : 'text-text-3'}`}>
      {children}
    </p>
  )
}

/** Neutral block-type tag — the same for every type, no colour per type. */
export function TypeBadge({ type }: { type: BlockType }) {
  return (
    <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded border border-line-strong px-1.5 py-px text-[11px] leading-4 text-text-2">
      {PANEL_TYPE_META[type]?.label ?? type}
    </span>
  )
}

/** Project scope as mono keys, or "any" when unscoped. */
export function ScopeChips({ scope }: { scope: string[] }) {
  if (!scope || scope.length === 0) return <span className="text-text-3">any</span>
  return <span className="font-mono text-[12px]">{scope.join(', ')}</span>
}

/** Copy-to-clipboard control with a short "Copied" confirmation. */
export function CopyButton({
  value,
  label = 'Copy URL',
  text = 'Copy',
  className,
}: {
  value: string
  /** accessible name */
  label?: string
  /** visible text */
  text?: string
  className?: string
}) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard blocked (insecure context) — no-op */
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label={label}
      className={[
        'inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[12px] transition-colors hover:bg-surface-active hover:text-foreground',
        copied ? 'text-ok' : 'text-text-2',
        className ?? '',
      ].join(' ')}
    >
      {copied ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
      <span aria-live="polite">{copied ? 'Copied' : text}</span>
    </button>
  )
}

/** One embed URL: truncated mono value + Copy. */
export function UrlRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="mb-1 text-[11.5px] text-text-3">{label}</div>
      <div className="flex min-w-0 items-center gap-2 rounded-md bg-surface py-1 pl-2.5 pr-1">
        <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-text-2" title={value}>{value}</span>
        <CopyButton value={value} label={`Copy ${label}`} />
      </div>
    </div>
  )
}

/**
 * Compact segmented control (radio-group semantics) — self/parent/child, density,
 * theme, etc. `size="sm"` is the tighter variant used inside table rows.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  size = 'default',
  ariaLabel,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
  size?: 'sm' | 'default'
  ariaLabel?: string
}) {
  const pad = size === 'sm' ? 'px-2 py-0.5 text-[11.5px]' : 'px-2.5 py-1 text-[12px]'
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="inline-flex overflow-hidden rounded-md border border-line-strong">
      {options.map((o) => {
        const on = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={[
              pad,
              'border-r border-line-strong transition-colors last:border-r-0 focus-visible:-outline-offset-2',
              on ? 'bg-surface-active text-foreground' : 'text-text-2 hover:text-foreground',
            ].join(' ')}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

/** Native select in the console style. */
export const selectClass =
  'h-7 rounded-md border border-input bg-surface px-2 text-[12px] text-foreground disabled:opacity-60'
